import {
  LINE_HEIGHT_PX,
  MIN_LINES_TO_START_ROW,
  LETTER_LETTERHEAD_TEMPLATE,
  resolvePrintTemplate,
  shouldReserveStationeryBands,
  type PageTemplate,
} from './page-template';
import { getPlannerUsableHeightPx, measurePrintBlocks, measureQuotationBlocks } from './measure';
import type {
  MeasuredBlocks,
  PagePlan,
  PagePlanDiagnostics,
  PageType,
  PlannedPage,
  PlannedRowSegment,
  PrintDocumentModel,
  QuotationPrintModel,
} from './types';
import { quotationToPrintDocument } from './adapters/quotation-adapter';

export type BuildPagePlanOptions = {
  template?: PageTemplate;
  measured?: MeasuredBlocks;
  /** Rendered body height (letterhead artwork measured) instead of the template's reserved bands. */
  usableHeightPx?: number;
  diagnostics?: boolean;
};

type MutablePage = {
  type: PageType;
  sectionIds: string[];
  itemRows: PlannedRowSegment[];
  tableHeaderIndexes: number[];
  used: number;
};

function finalizePages(pages: MutablePage[], usable: number): PlannedPage[] {
  return pages.map((p, pageIndex) => {
    let type: PageType = p.type;
    if (pageIndex === pages.length - 1) type = pages.length === 1 ? 'FIRST' : 'FINAL';
    else if (pageIndex === 0) type = 'FIRST';
    else type = 'CONTINUATION';
    return {
      type,
      pageIndex,
      sectionIds: p.sectionIds,
      sections: p.sectionIds,
      itemRows: p.itemRows,
      tableHeaderIndexes: p.tableHeaderIndexes,
      usedHeightPx: p.used,
      unusedHeightPx: Math.max(0, usable - p.used),
    };
  });
}

function remaining(page: MutablePage, usable: number): number {
  return usable - page.used;
}

function pushSectionId(page: MutablePage, id: string, height: number): void {
  if (!page.sectionIds.includes(id)) page.sectionIds.push(id);
  page.used += height;
}

/**
 * Document-agnostic two-pass planner.
 */
export function buildPagePlan(
  model: PrintDocumentModel,
  options: BuildPagePlanOptions = {}
): PagePlan {
  const template = options.template ?? resolvePrintTemplate(model.templateId);
  const usable =
    options.usableHeightPx ??
    getPlannerUsableHeightPx(template, {
      // Letter stationery (incl. preprinted) keeps header/footer bands; A4 only when artwork is present.
      reserveChromeBands: shouldReserveStationeryBands(model),
    });
  const measured = options.measured ?? measurePrintBlocks(model, { template });
  const warnings: string[] = [];
  const pages: MutablePage[] = [];

  const startFirst = (): MutablePage => {
    const page: MutablePage = {
      type: 'FIRST',
      sectionIds: [],
      itemRows: [],
      tableHeaderIndexes: [],
      used: 0,
    };
    for (const s of model.firstPageSections) {
      const h = measured.firstSections[s.id] ?? s.heightPx;
      pushSectionId(page, s.id, h);
    }
    pushSectionId(page, 'pageNumber', measured.pageNumber);
    pages.push(page);
    return page;
  };

  const startContinuation = (): MutablePage => {
    const page: MutablePage = {
      type: 'CONTINUATION',
      sectionIds: [],
      itemRows: [],
      tableHeaderIndexes: [],
      used: 0,
    };
    pushSectionId(page, 'continuationChrome', measured.continuationChrome);
    pushSectionId(page, 'pageNumber', measured.pageNumber);
    pages.push(page);
    return page;
  };

  let page = startFirst();

  const ensureTableHeader = (tableIndex: number) => {
    if (page.tableHeaderIndexes.includes(tableIndex)) return;
    if (remaining(page, usable) < measured.tableHeader + LINE_HEIGHT_PX * MIN_LINES_TO_START_ROW) {
      page = startContinuation();
    }
    if (!page.tableHeaderIndexes.includes(tableIndex)) {
      page.tableHeaderIndexes.push(tableIndex);
      page.used += measured.tableHeader;
      if (!page.sectionIds.includes('tableHeader')) page.sectionIds.push('tableHeader');
    }
  };

  const placeSegment = (segment: PlannedRowSegment) => {
    const needsHeader = !page.tableHeaderIndexes.includes(segment.tableIndex);
    const headerCost = needsHeader ? measured.tableHeader : 0;
    if (remaining(page, usable) < headerCost + segment.heightPx) {
      page = startContinuation();
    }
    ensureTableHeader(segment.tableIndex);
    if (remaining(page, usable) < segment.heightPx && segment.heightPx > usable * 0.9) {
      const row = model.tables[segment.tableIndex]?.rows[segment.itemIndex];
      warnings.push(
        `Row ${row?.lineNumber ?? segment.itemIndex + 1}: segment taller than usable page (${Math.round(segment.heightPx)}px).`
      );
    }
    page.itemRows.push(segment);
    page.used += segment.heightPx;
  };

  for (let tableIndex = 0; tableIndex < model.tables.length; tableIndex++) {
    const table = model.tables[tableIndex];
    const fulls = measured.itemFull[tableIndex] || [];
    const chunksAll = measured.itemChunks[tableIndex] || [];
    const chromes = measured.itemRowChrome[tableIndex] || [];

    // Optional table title as a first-page-only section cost already in firstSections;
    // if title exists and we're mid-doc, add a small title cost when starting the table.
    if (table.title && tableIndex > 0) {
      const titleH = 22;
      if (remaining(page, usable) < titleH + measured.tableHeader) {
        page = startContinuation();
      }
      pushSectionId(page, `tableTitle:${table.id}`, titleH);
    }

    for (let itemIndex = 0; itemIndex < table.rows.length; itemIndex++) {
      const fullH = fulls[itemIndex] ?? LINE_HEIGHT_PX;
      const chunkHeights = chunksAll[itemIndex] ?? [LINE_HEIGHT_PX];
      const chrome = chromes[itemIndex] ?? 22;
      const headerCost = page.tableHeaderIndexes.includes(tableIndex) ? 0 : measured.tableHeader;
      const space = remaining(page, usable) - headerCost;

      if (fullH <= space) {
        placeSegment({
          tableIndex,
          itemIndex,
          chunkStart: 0,
          chunkEnd: chunkHeights.length,
          isContinuation: false,
          showCommercial: true,
          heightPx: fullH,
        });
        continue;
      }

      const freshUsable =
        usable - measured.continuationChrome - measured.pageNumber - measured.tableHeader;
      if (fullH <= freshUsable) {
        placeSegment({
          tableIndex,
          itemIndex,
          chunkStart: 0,
          chunkEnd: chunkHeights.length,
          isContinuation: false,
          showCommercial: true,
          heightPx: fullH,
        });
        continue;
      }

      let chunkStart = 0;
      let firstSeg = true;
      while (chunkStart < chunkHeights.length) {
        const segHeaderCost = page.tableHeaderIndexes.includes(tableIndex) ? 0 : measured.tableHeader;
        let avail = remaining(page, usable) - segHeaderCost;
        if (avail < chrome + LINE_HEIGHT_PX * MIN_LINES_TO_START_ROW) {
          page = startContinuation();
          avail = remaining(page, usable) - measured.tableHeader;
        }

        const budget = Math.max(LINE_HEIGHT_PX, avail - (firstSeg ? chrome : Math.min(chrome, 18)));
        let chunkEnd = chunkStart;
        let descH = 0;
        while (chunkEnd < chunkHeights.length) {
          const next = chunkHeights[chunkEnd] ?? LINE_HEIGHT_PX;
          if (descH + next > budget && chunkEnd > chunkStart) break;
          if (descH + next > budget && chunkEnd === chunkStart) {
            chunkEnd += 1;
            descH += next;
            break;
          }
          descH += next;
          chunkEnd += 1;
        }

        placeSegment({
          tableIndex,
          itemIndex,
          chunkStart,
          chunkEnd,
          isContinuation: !firstSeg,
          showCommercial: firstSeg,
          heightPx: (firstSeg ? chrome : Math.min(chrome, 18)) + descH,
        });
        firstSeg = false;
        chunkStart = chunkEnd;
      }
    }
  }

  // Final keep-together groups
  const finals = model.finalSections;
  let i = 0;
  while (i < finals.length) {
    const group: typeof finals = [finals[i]];
    let height = measured.finalSections[finals[i].id] ?? finals[i].heightPx;
    while (finals[i].keepTogetherWithNext && i + 1 < finals.length) {
      i += 1;
      group.push(finals[i]);
      height += measured.finalSections[finals[i].id] ?? finals[i].heightPx;
    }
    const forceNewPage = group.some((s) => s.startOnNewPage);
    if (forceNewPage && (page.sectionIds.length > 0 || page.itemRows.length > 0 || page.used > 0)) {
      page = startContinuation();
    } else if (remaining(page, usable) < height) {
      const remainingPx = remaining(page, usable);
      const isLastFinalGroup = i >= finals.length - 1;
      // Estimates for compact approval blocks often exceed rendered height; avoid orphaning
      // signers on page 2. Never squeeze tall blocks (e.g. long notes) — overflow:hidden clips them.
      const canSqueezeLastFinal =
        isLastFinalGroup &&
        group.length === 1 &&
        height <= 160 &&
        remainingPx > 0 &&
        (remainingPx >= height * 0.45 || remainingPx >= 56);
      if (!canSqueezeLastFinal) {
        page = startContinuation();
      }
    }
    for (const s of group) {
      pushSectionId(page, s.id, measured.finalSections[s.id] ?? s.heightPx);
    }
    i += 1;
  }

  if (pages.length > 1 && pages[0] && pages[0].itemRows.length === 0) {
    const unusedRatio = (usable - pages[0].used) / usable;
    if (unusedRatio > 0.45) {
      warnings.push('Page 1 has no item rows while content continues — check measurement.');
    }
  }

  const planned = finalizePages(pages, usable);
  const diagnostics: PagePlanDiagnostics | undefined =
    options.diagnostics || warnings.length
      ? {
          usableHeightPx: usable,
          sectionHeights: { ...measured.firstSections, ...measured.finalSections },
          rowHeights: measured.itemFull.flat(),
          warnings,
        }
      : undefined;

  return { pages: planned, totalPages: planned.length, diagnostics };
}

/** Quotation convenience wrapper (keeps existing call sites). */
export function buildQuotationPagePlan(
  model: QuotationPrintModel,
  options: BuildPagePlanOptions = {}
): PagePlan {
  const doc = quotationToPrintDocument(model);
  const measured = options.measured ?? measureQuotationBlocks(model, options);
  // Remap legacy MeasuredBlocks shape if tests pass old shape — measureQuotationBlocks now returns generic.
  return buildPagePlan(doc, { ...options, measured });
}
